import { useMemo } from 'react';

import { ScreenCardInView } from 'containers/Intelligence/filters/screenFilterSignature';

import { useOrbitContext } from './OrbitContext';
import { ORBIT_CONTEXT_DETECTION_ENABLED } from './constants';
import { OrbitStarterGrants, hasSegmentDimension } from './promptStarters';
import useReportsGrant from './useReportsGrant';

export interface OrbitStarterContextValue {
  /** The screen the next question will be sent under; `undefined` on a route Orbit does not know. */
  screen: string | undefined;
  /** The cards that same question will be read against, in reading order. */
  cards: ScreenCardInView[];
  grants: OrbitStarterGrants;
}

const NO_CARDS: ScreenCardInView[] = [];

/**
 * What the prompt starters are chosen for: the screen and the cards the NEXT question will actually
 * carry, and the grants that decide which starters may be offered at all.
 *
 * It is the context `ask()` sends, not the live route: once the conversation is pinned every later
 * question goes under `sessionContext`, so suggesting questions about the screen the reader has
 * scrolled to would offer prompts the answer would not be about (PLA-9563 §1c/§2a). Shared by all
 * four places that draw starters so none of them can key on something different.
 */
export const useOrbitStarters = (): OrbitStarterContextValue => {
  const { entryContext, entryFilters, sessionContext, sessionCards, contextPinned } =
    useOrbitContext();
  const reports = useReportsGrant();

  const enabled = ORBIT_CONTEXT_DETECTION_ENABLED;
  const pinned =
    enabled && contextPinned && sessionContext !== null && sessionContext.screen !== undefined;
  const screen = enabled ? (pinned ? sessionContext?.screen : entryContext.screen) : undefined;
  const cards = (enabled ? (pinned ? sessionCards : entryFilters?.cards) : undefined) ?? NO_CARDS;

  const grants = useMemo<OrbitStarterGrants>(
    () => ({ reports, segmentDimension: hasSegmentDimension(cards) }),
    [reports, cards]
  );

  return { screen, cards, grants };
};

export default useOrbitStarters;
